import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import { AlertController, ModalController, Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { TranslocoService } from '@ngneat/transloco';
import {
  ColumnMode,
  DataTableColumnHeaderDirective,
  DatatableComponent,
  SelectionType,
  SortType,
} from '@swimlane/ngx-datatable';

import {
  Columns,
  ConnectionPool,
  EntityMetadata,
  PageInformation,
  SourceOfResult,
  TableCustomButton,
} from '@telus/d2c-core';
import { AuthService } from '@telus/d2c-core/auth';
import { ColumnType } from '@telus/d2c-core/lib/common';
import { IModel, PageAction } from '@telus/d2c-core/models';
import { AlertService } from '../services/alert.service';
import { environment } from 'env/config';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { ColumnFilterComponent } from './column-visibility/column-filter';
import { ColumnVisivilityComponent } from './column-visibility/column-visibility';
import { sortBy, isString, reduce, get, isEqual } from 'lodash';

const comparator = (propA, propB) => {
  const valueA = propA.text
    ? isString(propA.text)
      ? propA.text.toLowerCase()
      : propA.text
    : propA;
  const valueB = propB.text
    ? isString(propB.text)
      ? propB.text.toLowerCase()
      : propB.text
    : propB;
  // Just a simple sort function comparisons
  if (valueA < valueB) {
    return -1;
  }
  if (valueA > valueB) {
    return 1;
  }
};

@Component({
  selector: 'telus-table',
  templateUrl: 'table.html',
  styleUrls: ['./table.scss'],
})
export class TableComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild(DatatableComponent) ngxDatatable: DatatableComponent;

  @Input() tableId: string = null;
  @Input() externalSearchText = '';
  @Input() type: string;
  @Input() actions: string[];
  @Input() hasBulkActions: boolean;
  @Input() showBulkActionsButton: boolean;
  @Input() showToggleButton: boolean;
  @Input() disableBulkActions: boolean;
  @Input() showBulkSaveButton: boolean;
  @Input() showOnlyDeleteIcon = false;
  @Input() showNewButton = true;
  @Input() customButton: TableCustomButton;
  @Input() items: IModel[];
  @Input() resultModel: IModel[];
  @Input() labelText = '';
  @Input() checkboxLimit = -1;
  @Input() showFilterList = false;
  @Input() showSearchbar = true;
  @Input() isLoading: boolean;
  @Input() isSearching = false;
  @Input() filterData;
  @Input() headerCheckboxable = false;
  @Input() checkboxable = false;
  @Input() rowIdentity: any;
  @Input() previewItems = [];
  @Input() selectionType = SelectionType.checkbox;

  @Input() allowColumnSearch = true;
  @Input() allowSortBy = false;
  @Input() refreshFilter = new EventEmitter();
  @Input() saveButtonText: string = null;
  @Input() selected: IModel[] = [];
  @Input() newButtonLabel = '';
  @Input() beFilters = {
    list: [],
    dict: {},
    results: [],
    all: [],
    anyFilterIsActive: false,
  };
  @Input() useBackendFilters = false;
  @Input() pagination: PageInformation = {
    meta: {
      itemCount: environment.app.pageSize,
      totalItems: 0,
      itemsPerPage: environment.app.pageSize,
      totalPages: 0,
      currentPage: 0,
    },
    links: null,
    source: null,
  };

  @Output() search = new EventEmitter<string>();
  @Output() newItem = new EventEmitter();
  @Output() customButtonClick = new EventEmitter();
  @Output() editItem = new EventEmitter<string>();
  @Output() deleteItem = new EventEmitter<string>();
  @Output() detailItem = new EventEmitter<string>();
  @Output() selectedAction = new EventEmitter();
  @Output() getData = new EventEmitter<any>();
  @Output() pageChanged = new EventEmitter<any>();
  @Output() selectedItems = new EventEmitter();
  @Output() filterOptionSelected = new EventEmitter();
  @Output() toggleEvent = new EventEmitter<boolean>();

  @Output() getBackendFilters = new EventEmitter();

  @Output() totalFiltered = new EventEmitter();

  originalItems: IModel[] = [];

  size = 12;
  mobile: boolean;
  searchText = '';
  previousPage = 0;

  tableOffset = 0;

  // THIS IS TO ACCESS THE LANGUAGE OBJECT USED BY TRANSLOCO
  colLang = '.columns.';
  button = '.newButton';

  activeColumns: Columns[];
  allColumns: Columns[];
  columnFilterOptions = {};

  checkedItems = [];
  selectedItemsPerPage = {};
  isIndeterminate = false;
  showSkeleton = true;

  typeOfColumns = ColumnType;
  style = '';
  timeOut: any;
  rows = [];
  math = Math;

  columnMode = ColumnMode;
  sortType = SortType;
  allowedActions = [];

  canCreate: boolean = null;
  canDelete: boolean = null;
  canUpdate: boolean = null;
  canFetch: boolean = null;

  futureOffset = 0;

  sourceOfResult = SourceOfResult;
  filters = {
    list: [],
    dict: {},
    results: [],
    all: [],
    anyFilterIsActive: false,
  };
  possibleColumnsToShown = [];
  filtersForFENeedToBeUpdated = false;
  filtersApplied = false;
  customFilters = [];
  customSorting = [];
  selectedColumnToShowFilter = '';

  readonly headerHeight = 56;
  readonly rowHeight = 55;
  readonly totalWidth = 12;
  private lastSelection: IModel[] = [];
  private colSub: Subscription;

  constructor(
    private el: ElementRef,
    private storage: Storage,
    private transService: TranslocoService,
    private alertService: AlertService,
    private alertCtrl: AlertController,
    private platform: Platform,
    public modalCtrl: ModalController,
    private pool: ConnectionPool,
    public authService: AuthService,
    private router: Router
  ) {}

  public linkComparator(a, b) {
    return comparator(a, b);
  }

  /**
   * Uses row id to persist
   * checkbox selection.
   *
   * This is the default behavior, that uses
   * the whole row to identify itself.
   * ngx-table's property rowIdentity
   */
  getRowId(row: any) {
    return row;
  }

  canDoAction(action: PageAction) {
    return this.authService.canDoAction(this.tableId, action);
  }

  validatePagination() {
    if (!this.pagination || this.pagination.meta.totalItems === 0) {
      this.pagination = {
        meta: {
          itemCount: environment.app.pageSize,
          totalItems: this.items.length,
          itemsPerPage: environment.app.pageSize,
          totalPages: 1,
          currentPage: 0,
        },
        links: null,
        source: SourceOfResult.internal,
      };
    }

    if (this.pagination.meta.totalItems < this.originalItems.length) {
      this.pagination.meta.totalItems = this.originalItems.length;
    }

    if (
      this.futureOffset * this.pagination.meta.itemsPerPage >=
      this.originalItems.length
    ) {
      this.futureOffset = 0;
    }
  }

  ngOnInit() {
    this.canCreate = this.authService.canCreate(this.tableId);
    this.canDelete = this.authService.canDelete(this.tableId);
    this.canUpdate = this.authService.canUpdate(this.tableId);
    this.canFetch = true; //this.authService.canFetch(this.tableId);
    // TODO: User and Role are not using convention so this permission will break the app.

    this.customFilters = this.getCustomColumnFilters(this.tableId);
    this.customSorting = this.getCustomColumnSorting(this.tableId);

    if (!this.items) {
      this.items = [];
      this.originalItems = [];
    }

    if (this.saveButtonText) {
      this.button = this.saveButtonText;
    }
    this.totalFiltered.emit(this.items.length);

    this.allowedActions = this.actions.filter((actionName) =>
      this.canDoAction(actionName as PageAction)
    );

    this.originalItems = [...this.items];
    this.searchText = '';
    this.mobile =
      this.platform.platforms().includes('ios') ||
      this.platform.platforms().includes('android');

    this.retrieveAndSetColumnsFromStorage().then(() => {
      this.validatePagination();
      this.setPage({ offset: 0 });
    });
  }

  async retrieveAndSetColumnsFromStorage(columns?: Columns[]) {
    const cols = await this.storage.get(this.type);
    let visibleColumns: Columns[] =
      columns && columns.length > 0 ? columns : JSON.parse(cols);

    let tableId = this.tableId;
    if (tableId === 'd2Cgeoserviceaddressreviewtable') {
      tableId = 'd2Cgeoserviceaddress';
    }

    if (!visibleColumns || visibleColumns.length === 0) {
      const metadata: EntityMetadata = this.pool.getMetadata(tableId);

      if (metadata) {
        visibleColumns = metadata.getTelusTableColumns();
      } else {
        visibleColumns = [];
      }
    }

    visibleColumns.forEach((column) => {
      if (column.type === this.typeOfColumns.link) {
        column.comparator = this.linkComparator.bind(this);
      } else {
        column.comparator = undefined;
      }
    });

    const activeColumns = visibleColumns.filter((column) => column.visible);
    this.allColumns = visibleColumns.filter(
      (column) => !column.hiddenTableField
    );

    this.possibleColumnsToShown = this.allColumns
      .filter((column) => column.visible && column.type !== 'array')
      .map((column) => column.field);

    // Add delete button to columns
    if (this.allowedActions.length > 0 && !this.mobile) {
      activeColumns.push({
        field: 'actions',
        visible: true,
        type: '',
        hiddenFormField: false,
        hiddenTableField: false,
        isEditable: false,
      });
    }

    this.activeColumns = activeColumns;
    const length = this.activeColumns.length;
    this.size = this.totalWidth / length;
  }

  message() {
    this.isLoading = false;
  }

  onRowClick(click) {
    const { type, row } = click;

    if (type === 'checkbox') {
      click.event.stopPropagation();
    } else {
      if (type === 'click') {
        if (this.checkboxable) {
          this.manageSelected(row);
        } else {
          this.onDetail(row.id);
        }
      }
    }
  }

  manageSelected(row) {
    const selected = [...this.ngxDatatable.selected];
    const index = selected.findIndex((item) => item.id === row.id);

    if (index < 0) {
      selected.push(row);
    } else {
      selected.splice(index, 1);
    }

    this.ngxDatatable.select.emit({ selected });
  }

  async showColumnsVisibilityFilter() {
    const loadingEl = await this.alertService.getLoadingCtrl('loading');
    await loadingEl.present();
    await this.retrieveAndSetColumnsFromStorage();

    const tableId =
      this.tableId === 'd2Cgeoserviceaddressreviewtable'
        ? 'd2Cgeoserviceaddress'
        : this.tableId;
    const metadata = this.pool.getMetadata(tableId);
    const defaultVisibleColumns = metadata
      .getTelusTableColumns()
      .filter((c) => c.visible);

    const modal = await this.modalCtrl.create({
      component: ColumnVisivilityComponent,
      canDismiss: true,
      presentingElement: await this.modalCtrl.getTop(),
      componentProps: {
        columnSelection: this.allColumns,
        defaultVisibleColumns,
        pageType: this.type + this.colLang,
      },
    });
    loadingEl.dismiss();
    await modal.present();

    const { data } = await modal.onWillDismiss();
    if (data) {
      const loadingElData = await this.alertService.getLoadingCtrl('loading');
      loadingElData.present();
      this.filtersForFENeedToBeUpdated = true;
      await this.storage.set(this.type, JSON.stringify(data));
      await this.retrieveAndSetColumnsFromStorage(data);
      loadingElData.dismiss();
    }
  }

  onNewItem() {
    this.newItem.emit();
  }

  onClickCustomButton() {
    this.customButtonClick.emit();
  }

  onEdit(id: number | string) {
    if (this.canUpdate) {
      this.editItem.emit(String(id));
    }
  }

  async onDelete(index, event) {
    event.stopPropagation();
    if (!this.canDelete) {
      return;
    }
    const id = this.items[index].id;

    const alertEl = await this.alertCtrl.create({
      header: this.transService.translate('deleteHeader'),
      message: this.transService.translate('deleteMessage'),
      buttons: [
        {
          text: this.transService.translate('confirm'),
          handler: () => {
            this.deleteItem.emit(String(id));
            this.isLoading = false;
          },
        },
        {
          text: this.transService.translate('cancel'),
        },
      ],
    });
    alertEl.present();
  }

  onDetail(id: string) {
    this.detailItem.emit(id);
  }

  async onSelect({ selected }) {
    const currentPage = this.pagination.meta.currentPage;

    // TODO: Ben, can you check if you need to keep the object?
    //       if not we can just simply use IDs in here
    this.selectedItemsPerPage[currentPage] = [...selected];
    const keys = Object.keys(this.selectedItemsPerPage);

    let totalItems = 0;
    keys.forEach((key) => {
      totalItems = totalItems + this.selectedItemsPerPage[key].length;
    });

    if (this.checkboxLimit > -1 && totalItems > this.checkboxLimit) {
      const alert = await this.alertCtrl.create({
        header: this.transService.translate('errorHeader'),
        message:
          this.transService.translate('Onlyupto') +
          ` ${this.checkboxLimit} ` +
          this.transService.translate('addressescanbeselected'),
        buttons: [
          {
            text: this.transService.translate('ok'),
            handler: () => {
              this.selectedItemsPerPage[currentPage] = [...this.lastSelection];
              this.selected = [...this.lastSelection];
            },
          },
        ],
      });

      await alert.present();
      return;
    } else {
      this.selected = [...selected];
      this.lastSelection = [...selected];

      // TODO: we need to check if we need to send all the items
      //       if that is the case, uncomment this part
      this.checkedItems = [];
      for (const key of keys) {
        this.checkedItems = [
          ...this.checkedItems.concat(this.selectedItemsPerPage[key]),
        ];
      }

      this.selectedItems.emit({
        items: this.checkedItems,
        action: 'selected',
        id: '',
        total: totalItems,
        totalItemsSelected: this.checkedItems,
        pageSelection: selected,
      });
    }
  }

  onAction(action: string) {
    this.selectedAction.emit({
      action,
      items: this.checkedItems,
      total: this.checkedItems.length,
    });
  }

  onFilterOption(option) {
    this.filterOptionSelected.emit(option);
  }

  onToggle(active: boolean) {
    this.toggleEvent.emit(active);
  }

  getFilterValue({ target: { value } }) {
    this.searchText = (value as string).toLowerCase();
    this.showSkeleton = true;
    this.search.emit(this.searchText);
  }

  searchEnter(e: Event) {
    e.preventDefault();
    this.showSkeleton = true;
    this.search.emit(this.searchText);
  }

  ngOnDestroy() {
    if (this.colSub) {
      this.search.emit('');
      this.toggleEvent.emit(true);
      this.colSub.unsubscribe();
    }
  }

  preventUpdate() {
    this.checkedItems = [...this.lastSelection];
    this.selected = [...this.lastSelection];
    this.tableOffset = 0;
  }

  onActivate(event) {}

  async ngOnChanges(changes: SimpleChanges) {
    if (
      changes.items &&
      changes.items.currentValue &&
      changes.items.currentValue.length &&
      changes.items.currentValue.length !== 0
    ) {
      this.showSkeleton = false;
      this.originalItems = changes.items.currentValue
        ? changes.items.currentValue
        : [];

      this.filtersForFENeedToBeUpdated = true;

      if (
        this.pagination?.source === SourceOfResult.geoserver &&
        this.pagination?.meta?.currentPage
      ) {
        this.setPage({ offset: this.pagination.meta.currentPage });
      } else {
        this.validatePagination();
        this.setPage({ offset: this.futureOffset });
      }
    }

    if (changes.tableId) {
      this.retrieveAndSetColumnsFromStorage();

      if (!changes.items) {
        this.showSkeleton = true;
      }
    }

    if (changes.selected) {
      // Reviews overall state of pagination state
      // and applies updates (additions/removals) to
      // the object this.selectedItemsPerPage
      const newValuesIds = (changes.selected.currentValue || []).map(
        (item) => item.id
      );
      const removedValues = (changes.selected.previousValue || [])
        .filter((item) => newValuesIds.indexOf(item.id) === -1)
        .map((item) => item.id);
      const addedValues = (changes.selected.currentValue || [])
        .filter((item) => removedValues.indexOf(item.id) === -1)
        .map((item) => item.id);
      let currPage = 0;
      for (const key of Object.keys(this.selectedItemsPerPage)) {
        const pageStart = currPage * this.pagination.meta.itemsPerPage;
        const pageEnd = pageStart + this.pagination.meta.itemsPerPage;
        const existentItems = this.selectedItemsPerPage[key].map(
          (item) => item.id
        );
        this.selectedItemsPerPage[key] = [
          ...this.selectedItemsPerPage[key],
          ...this.items
            .slice(pageStart, pageEnd)
            .filter(
              (item) =>
                (addedValues.indexOf(item.id) !== -1 &&
                  existentItems.indexOf(item.id) === -1) ||
                (addedValues.indexOf(item.id) === -1 &&
                  existentItems.indexOf(item.id) !== -1)
            ),
        ];
        if (removedValues.length > 0) {
          this.selectedItemsPerPage[key] = this.selectedItemsPerPage[
            key
          ].filter((item) => removedValues.indexOf(item.id) === -1);
        }
        currPage++;
      }

      if (
        addedValues.length !== 0 &&
        !this.selectedItemsPerPage[this.pagination?.meta?.currentPage] &&
        this.pagination &&
        this.pagination.links &&
        this.pagination.links.first &&
        this.pagination.links.first.indexOf('privilege') !== -1
      ) {
        // this is if checkbox have been previously (programatically)
        // checked
        this.selected = changes.selected.currentValue;
      } else {
        this.selected =
          this.selectedItemsPerPage[this.pagination?.meta?.currentPage] || [];
      }

      this.checkedItems = [];
      for (const key of Object.keys(this.selectedItemsPerPage)) {
        this.checkedItems = [
          ...this.checkedItems.concat(this.selectedItemsPerPage[key]),
        ];
      }
      this.lastSelection = [...this.selected];

      if (this.selectionType === SelectionType.single) {
        this.selected = [...changes.selected.currentValue];
      }
    }

    if (
      (changes.isLoading && changes.isLoading.currentValue === false) ||
      (this.pagination?.meta?.totalPages &&
        this.futureOffset === this.pagination.meta.totalPages - 1)
    ) {
      this.isLoading = false;
      this.showSkeleton = false;
    }

    this.isLoading = false;
  }

  onLinkClick(e: Event, link) {
    e.stopPropagation();
    e.stopImmediatePropagation();

    this.router.navigate([link]);
  }

  displayCheck(row) {
    return true;
  }

  formattedDate(date) {
    return date
      ? moment(date).format(environment.app.defaultAddressFormat)
      : '';
  }

  setPreviousPage(pageInfo) {
    this.previousPage = this.pagination.meta.currentPage;
    return true;
  }

  setPage(pageInfo) {
    this.isLoading = false;
    this.showSkeleton = false;

    const pagination = {
      meta: {
        ...this.pagination.meta,
        currentPage: pageInfo.offset,
      },
      links: this.pagination.links,
      source: this.pagination.source,
    };
    this.pagination = pagination;

    if (
      (this.futureOffset !== pageInfo.offset &&
        this.pagination.source !== SourceOfResult.reproster) ||
      this.pagination.source === SourceOfResult.reproster ||
      pageInfo.offset === 0 ||
      (this.futureOffset === this.pagination.meta.totalPages - 1 &&
        this.items.length === this.pagination.meta.totalItems)
    ) {
      const start = pageInfo.offset * this.pagination.meta.itemsPerPage;

      const items =
        this.filters.results.length !== 0 || this.filters.anyFilterIsActive
          ? this.filters.results
          : this.items;

      this.rows = items.slice(start, start + this.pagination.meta.itemsPerPage);
    } else {
      this.rows = this.resultModel;
    }

    const selectedItems = this.selectedItemsPerPage[pageInfo.offset];
    this.selected = selectedItems ? [...selectedItems] : [];
    this.lastSelection = selectedItems ? [...selectedItems] : [];
    if (
      pageInfo.offset !== 0 &&
      this.pagination.source !== SourceOfResult.internal
    ) {
      //check if already loaded
      const futureLength =
        pageInfo.offset * this.pagination.meta.itemsPerPage +
        this.pagination.meta.itemsPerPage;

      if (
        this.items.length < futureLength &&
        pageInfo.offset !== this.futureOffset
      ) {
        this.showSkeleton = true;
        this.futureOffset = pageInfo.offset;
        this.getData.emit({
          ...pageInfo,
          ...this.pagination.meta,
          source: this.pagination.source,
        });
        this.isLoading = true;
      }
    }

    pageInfo.previousPage = this.previousPage;
    this.pageChanged.emit(pageInfo);
  }

  isAnyTypeOfConditionActive() {
    return (
      this.filters.anyFilterIsActive === true ||
      this.beFilters.anyFilterIsActive === true ||
      this.filters.results.length !== 0 ||
      this.searchText.length !== 0
    );
  }

  // ----------------------------------------------
  // Sorting
  // ----------------------------------------------
  onSort($event) {
    if (this.allowSortBy !== true) {
      return;
    }

    this.isLoading = true;
    this.getData.emit({
      source: this.pagination.source,
      orderBy: $event.sorts,
    });
    this.isLoading = false;
  }

  // ----------------------------------------------
  // Filters
  // ----------------------------------------------

  async createFEFilters(items) {
    const fields = await this.getColumnsForFilters();

    let fieldsCounter;
    let itemsCounter = 0;
    let item;
    this.filters.all = items;
    for (fieldsCounter = 0; fieldsCounter < fields.length; fieldsCounter++) {
      this.filters.list.push(
        (this.filters.dict[fields[fieldsCounter]] = {
          list: [],
          dict: {},
          field: fields[fieldsCounter],
          searchText: '',
          active: false,
          options: [],
        })
      );
    }

    for (; itemsCounter < items.length; itemsCounter++) {
      for (fieldsCounter = 0; fieldsCounter < fields.length; fieldsCounter++) {
        item = this.filters.dict[fields[fieldsCounter]];

        let itemName = items[itemsCounter][fields[fieldsCounter]];
        itemName = itemName?.text ? itemName.text : itemName;
        itemName =
          itemName === undefined || itemName === '' || itemName === null
            ? ''
            : itemName.toString();

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        item.dict[itemName] ||
          item.list.push(
            (item.dict[itemName] = {
              title: itemName,
              checked: true,
              visible: false,
              match: false,
            })
          );
      }
    }

    this.filters.results = [];
  }

  filtrateFE() {
    return this.excelLikeColumnFiltration();
  }

  async clearFilters() {
    if (this.useBackendFilters) {
      await this.clearBEFilters();
    } else {
      await this.clearFEFilters();
    }
  }

  async clearBEFilters() {
    this.beFilters = {
      list: [],
      dict: {},
      results: [],
      all: [],
      anyFilterIsActive: false,
    };

    this.filtersApplied = false;
    this.setPage({ offset: 0 });

    this.getData.emit({
      source: this.pagination.source,
      cleanFilters: true,
      orderBy: [],
      beFilters: this.beFilters,
    });
  }

  async clearFEFilters() {
    const loadingEl = await this.alertService.getLoadingCtrl('loading');
    await loadingEl.present();
    this.filters = {
      list: [],
      dict: {},
      results: [],
      all: [],
      anyFilterIsActive: false,
    };
    await this.createFEFilters(this.originalItems);
    this.filtersApplied = false;
    this.filtrateFE();
    this.setPage({ offset: 0 });
    await loadingEl.dismiss();
  }

  async showFiltersByColumn(columnName, $event) {
    $event.stopPropagation();
    $event.stopImmediatePropagation();
    this.selectedColumnToShowFilter = columnName;
    if (this.useBackendFilters) {
      const loadingEl = await this.alertService.getLoadingCtrl('loading');
      await loadingEl.present();
      const fields =
        this.customFilters.length === 0
          ? this.possibleColumnsToShown
          : this.possibleColumnsToShown.filter((field) =>
              this.customFilters.includes(field)
            );
      this.getBackendFilters.emit({ columnName, fields });
      await loadingEl.dismiss();
    } else {
      await this.showFiltersByFEColumn(columnName);
    }
  }

  async showFiltersByBEColumn() {
    const loadingEl = await this.alertService.getLoadingCtrl('loading');
    await loadingEl.present();

    const modal = await this.modalCtrl.create({
      component: ColumnFilterComponent,
      canDismiss: true,
      presentingElement: await this.modalCtrl.getTop(),
      componentProps: {
        filters: this.beFilters,
        columnName: this.selectedColumnToShowFilter,
        pageType: this.type + this.colLang,
        sorted: true,
      },
    });

    await modal.present();
    await loadingEl.dismiss();

    const { data } = await modal.onWillDismiss();
    this.isLoading = true;

    if (data) {
      await loadingEl.present();
      this.filtersApplied = true;

      this.filtrateBE(data);
      this.setPage({ offset: 0 });
      await loadingEl.dismiss();
      this.isLoading = false;
    } else {
      await loadingEl.dismiss();
      this.isLoading = false;
    }
  }

  async showFiltersByFEColumn(columnName) {
    const loadingEl = await this.alertService.getLoadingCtrl('loading');
    await loadingEl.present();

    if (this.filtersForFENeedToBeUpdated) {
      await this.createFEFilters(this.originalItems);
      this.filtersForFENeedToBeUpdated = false;
    }

    this.filtrateFE();

    const modal = await this.modalCtrl.create({
      component: ColumnFilterComponent,
      canDismiss: true,
      presentingElement: await this.modalCtrl.getTop(),
      componentProps: {
        filters: this.filters,
        columnName,
        pageType: this.type + this.colLang,
        sorted: true,
      },
    });

    await modal.present();
    await loadingEl.dismiss();

    const { data } = await modal.onWillDismiss();
    this.isLoading = true;

    if (data) {
      await loadingEl.present();
      this.filtersApplied = true;
      this.filters = data;
      this.filtrateFE();
      this.setPage({ offset: 0 });
      await loadingEl.dismiss();
    }

    this.isLoading = false;
  }

  // ------------------------------------------------------
  // Private methods
  // ------------------------------------------------------

  private async getColumnsForFilters() {
    await this.retrieveAndSetColumnsFromStorage();
    const fields =
      this.customFilters.length === 0
        ? this.possibleColumnsToShown
        : this.possibleColumnsToShown.filter((field) =>
            this.customFilters.includes(field)
          );

    return fields;
  }

  private getCleanTableIdString(tableId: string) {
    return tableId.replace('-', '').toLowerCase();
  }

  private getCustomColumnFilters(tableId) {
    return get(
      environment.app.columns.filters,
      this.getCleanTableIdString(tableId),
      []
    );
  }

  private getCustomColumnSorting(tableId) {
    return get(
      environment.app.columns.sorting,
      this.getCleanTableIdString(tableId),
      []
    );
  }

  private filtrateBE(beFilters) {
    let i;
    let j;
    let k;
    let unselected;
    let selected;
    let filter;

    const unselectionDataFromFilters = {};
    const selectionDataFromFilters = {};
    const filters = beFilters.list;
    let anyFilterIsActive = false;
    for (j = 0; j < filters.length; j++) {
      filter = filters[j];

      for (k = 0, unselected = [], selected = []; k < filter.list.length; k++) {
        if (filter.list[k].checked === false) {
          if (filter.list[k]?.value) {
            unselected.push(filter.list[k].value);
          } else {
            unselected.push(filter.list[k].title);
          }
        } else {
          if (filter.list[k]?.value) {
            selected.push(filter.list[k].value);
          } else {
            selected.push(filter.list[k].title);
          }
        }
      }

      if (unselected.length > 0) {
        filter.isActive = true;
        anyFilterIsActive = true;
        unselectionDataFromFilters[filter.field] = unselected;
      }

      if (selected.length > 0) {
        selectionDataFromFilters[filter.field] = selected;
      }
    }

    beFilters.anyFilterIsActive = anyFilterIsActive;

    this.getData.emit({
      source: this.pagination.source,
      beFilters,
      unselectionDataFromFilters,
      selectionDataFromFilters,
    });
  }

  private simpleColumnFiltration() {
    let i;
    let j;
    let k;
    let selected;
    let filter;
    let option;
    const data = this.filters.all;
    const filters = this.filters.list;
    let anyFilterIsActive = false;

    this.filters.results = [];

    for (j = 0; j < filters.length; j++) {
      filter = filters[j];

      for (k = 0, selected = 0; k < filter.list.length; k++) {
        if (!filter.list[k].checked) {
          selected++;
          break;
        }
      }

      filter.options = filter.list;
      if (selected > 0) {
        filter.isActive = true;
        anyFilterIsActive = true;
      }
    }
    for (i = 0; i < data.length; i++) {
      const rejects = [];
      for (j = 0; j < filters.length; j++) {
        filter = filters[j];
        let value = data[i][filter.field];
        value = value && value.text ? value.text : value;
        value =
          value === undefined || value === '' || value === null
            ? ''
            : value.toString();
        option = filter.dict[value];

        if (!option.checked) {
          rejects.push(option);
          break;
        }
      }
      if (rejects.length === 0) {
        this.filters.results.push(data[i]);
      }
    }

    //Check if any filter is being applied
    this.filters.anyFilterIsActive = anyFilterIsActive;
  }

  private excelLikeColumnFiltration() {
    let i;
    let j;
    let k;
    let selected;
    let blocks;
    let filter;
    let option;
    const data = this.filters.all;
    const filters = this.filters.list;
    this.filters.results = [];
    const results = [];

    for (j = 0; j < filters.length; j++) {
      filter = filters[j];
      filter.regex = filter.searchText.length
        ? new RegExp(filter.searchText, 'i')
        : false;

      for (k = 0, selected = 0; k < filter.list.length; k++) {
        if (!filter.list[k].checked) {
          selected++;
        }
        filter.list[k].visible = false;
        filter.list[k].match = filter.regex
          ? filter.list[k].title.match(filter.regex)
          : true;
      }
      filter.isActive = filter.searchText.length > 0 || selected > 0;
    }
    for (i = 0; i < data.length; i++) {
      blocks = { allows: [], rejects: [], mismatch: false };
      for (j = 0; j < filters.length; j++) {
        filter = filters[j];
        let value = data[i][filter.field];
        value = value && value.text ? value.text : value;
        value =
          value === undefined || value === '' || value === null ? '' : value;
        option = filter.dict[value];
        (option.checked ? blocks.allows : blocks.rejects).push(option);
        if (filter.regex && !option.match) {
          blocks.mismatch = true;
        }
      }
      if (blocks.rejects.length === 1) {
        blocks.rejects[0].visible = true;
      } else if (blocks.rejects.length === 0 && !blocks.mismatch) {
        results.push(data[i]);
        blocks.allows.forEach((x) => {
          x.visible = true;
        });
      }
    }
    for (j = 0; j < filters.length; j++) {
      filter = filters[j];
      filter.options = [];
      for (k = 0; k < filter.list.length; k++) {
        if (filter.list[k].visible && filter.list[k].match) {
          filter.options.push(filter.list[k]);
        }
      }
    }

    //Check if any filter is being applied
    this.filters.anyFilterIsActive = reduce(
      this.filters.dict,
      (result, value: any) => {
        result = result || value.isActive;
        return result;
      },
      false
    );

    this.filters.results = results;
  }
}
